/**
 * The fourteen virtual classes, and the key format that identifies them.
 *
 * BHCS teaches Chinese and Math to every after-school child, split by grade: 2 subjects ×
 * 7 grades (TK, K, G1–G5). Nobody enrols in them. A child is in Chinese G3 because they
 * are in after-school and they are in third grade, full stop.
 *
 * So there is no `programs` row for any of them, no enrollment, no pricing and no
 * invoice — the catalog is this file. The only thing a virtual class persists is
 * attendance, written against the child's *after-school* `program_id` and
 * `enrollment_id` (the seat that actually puts them in the room) plus the
 * `virtual_class_key` below, which is what keeps the after-school, Chinese and Math rows
 * for one child on one day from colliding on the table's unique key.
 *
 * This module owns the key format on the app side. Its counterpart in the database is
 * `public.virtual_class_grade(text)`; the two must agree, because a key the database
 * cannot parse grants a teacher nothing and fails silently.
 */

import { normalizeGrade } from '../utils/rosterGrouping';

export interface VirtualSubject {
  id: 'chinese' | 'math';
  label: string;
  /** Shown on the group tab, matching how `programs.type` icons read elsewhere. */
  icon: string;
}

export const VIRTUAL_SUBJECTS: readonly VirtualSubject[] = [
  { id: 'chinese', label: 'Chinese', icon: '文' },
  { id: 'math', label: 'Math', icon: '📚' },
];

/**
 * The grades that run, in the order a roster is read.
 *
 * These are `normalizeGrade` outputs, not display labels — `'1'`, never `'G1'` — so they
 * compare directly against a normalized `students.metadata->>'grade'`.
 */
export const VIRTUAL_GRADES = ['TK', 'K', '1', '2', '3', '4', '5'] as const;

export type VirtualGrade = typeof VIRTUAL_GRADES[number];

export interface VirtualClass {
  /** `'chinese:3'` — the value stored in `attendance_records.virtual_class_key`. */
  key: string;
  subject: VirtualSubject;
  grade: VirtualGrade;
  /** `'Chinese G3'` — how the class is named to a teacher or an admin. */
  label: string;
}

/**
 * `'chinese:3'`, `'math:tk'` — lowercase `<subject>:<grade>`.
 *
 * Lowercase because the value is an identifier that ends up in a database column and in
 * URLs, and a key that differs only by case would silently split one class in two.
 */
export function virtualClassKey(subject: string, grade: string): string {
  return `${subject.toLowerCase()}:${grade.toLowerCase()}`;
}

/** `'TK'` → `'TK'`, `'3'` → `'G3'`. How a grade is written to a person. */
export function virtualGradeLabel(grade: string): string {
  return grade === 'TK' || grade === 'K' ? grade : `G${grade}`;
}

/**
 * The class a key names, or `null` when it names none.
 *
 * Unrecognized keys resolve to `null` rather than throwing: they reach this function from
 * `staff_users.virtual_class_keys`, which an admin edits, and a typo there should cost a
 * teacher one missing tab — not a portal that will not render.
 */
export function parseVirtualClassKey(key: string): VirtualClass | null {
  const [rawSubject, rawGrade] = (key ?? '').split(':');
  if (!rawSubject || !rawGrade) return null;

  const subject = VIRTUAL_SUBJECTS.find(s => s.id === rawSubject.toLowerCase());
  if (!subject) return null;

  const grade = normalizeGrade(rawGrade);
  if (grade === null) return null;
  if (!(VIRTUAL_GRADES as readonly string[]).includes(grade)) return null;

  return {
    key: virtualClassKey(subject.id, grade),
    subject,
    grade: grade as VirtualGrade,
    label: `${subject.label} ${virtualGradeLabel(grade)}`,
  };
}

/** All fourteen, subject-major then in grade order — how the admin picker lists them. */
export const ALL_VIRTUAL_CLASSES: readonly VirtualClass[] = VIRTUAL_SUBJECTS.flatMap(
  subject =>
    VIRTUAL_GRADES.map(grade => ({
      key: virtualClassKey(subject.id, grade),
      subject,
      grade,
      label: `${subject.label} ${virtualGradeLabel(grade)}`,
    })),
);

/**
 * The classes a set of keys resolves to, in `ALL_VIRTUAL_CLASSES` order and deduped.
 *
 * Order comes from the catalog rather than from the input so that two teachers holding
 * the same classes, stored in whatever order they were clicked, see the same tabs in the
 * same places.
 */
export function resolveVirtualClasses(
  keys: readonly string[] | null | undefined,
): VirtualClass[] {
  if (!keys || keys.length === 0) return [];
  const held = new Set(
    keys.map(k => parseVirtualClassKey(k)?.key).filter((k): k is string => Boolean(k)),
  );
  return ALL_VIRTUAL_CLASSES.filter(vc => held.has(vc.key));
}
